/**
 * @fileoverview Button component for user interactions
 * @module ctl-button
 * @version 1.0.0
 * 
 * @description
 * A versatile button component that supports multiple variants, sizes, loading states,
 * and can render as either a button or link element.
 * 
 * **Important Note on Shadow DOM CSS Inheritance:**
 * This component uses explicit CSS property resets instead of `all: unset` to preserve
 * CSS custom property (design token) inheritance from the document root. The `all: unset`
 * pattern resets inherited custom properties, causing theming to break in Shadow DOM.
 * 
 * Other components in the codebase (34+ instances across forms, overlays, navigation, etc.)
 * still use `all: unset` and may experience similar theming issues. This pattern should be
 * systematically replaced across the component library.
 * 
 * @example
 * <!-- Primary button -->
 * <ctl-button variant="primary" size="md">Click me</ctl-button>
 * 
 * @example
 * <!-- Button with icon -->
 * <ctl-button variant="secondary">
 *   <ctl-icon slot="icon" name="plus"></ctl-icon>
 *   Add Item
 * </ctl-button>
 * 
 * @example
 * <!-- Button as link -->
 * <ctl-button href="/dashboard" variant="tertiary">Go to Dashboard</ctl-button>
 * 
 * @example
 * <!-- Loading button -->
 * <ctl-button loading variant="primary">Saving...</ctl-button>
 * 
 * @example
 * <!-- Disabled button -->
 * <ctl-button disabled variant="danger">Delete</ctl-button>
 */

import { dispatchEvent } from '../../../utils/events.js';

const template = document.createElement("template");

template.innerHTML = `
<style>
  :host {
    /* Use consistent spacing scale from design tokens */
    --ctl-btn-radius: var(--ctl-radius-md);
    --ctl-btn-font-weight: var(--ctl-font-weight-semibold);
    --ctl-btn-gap: var(--ctl-space-2);

    display: inline-block;
  }

  button,
  a {
    /* Reset default button/anchor styles explicitly instead of 'all: unset' 
       to preserve CSS custom property inheritance from the document root */
    margin: 0;
    padding: 0;
    background: none;
    border: none;
    text-decoration: none;
    font: inherit;
    color: inherit;
    cursor: pointer;
    appearance: none; /* Removes default platform styling, especially important for anchors on mobile Safari */
    box-sizing: border-box;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: var(--ctl-btn-gap);
    border-radius: var(--ctl-btn-radius);
    font-weight: var(--ctl-btn-font-weight);
    transition: background var(--ctl-transition-normal),
                color var(--ctl-transition-normal),
                border-color var(--ctl-transition-normal),
                opacity var(--ctl-transition-normal);
    user-select: none;
    border: var(--border-width-thin) solid transparent;
    min-height: var(--ctl-min-touch-target);
    min-width: var(--ctl-min-touch-target);
  }

  button:focus-visible,
  a:focus-visible {
    outline: var(--ctl-focus-ring-width) solid var(--ctl-color-focus);
    outline-offset: var(--ctl-focus-ring-offset);
  }

  /* All button sizes meet 44px touch target minimum for accessibility */
  :host([size="sm"]) button,
  :host([size="sm"]) a { padding: var(--ctl-button-padding-sm); font-size: var(--font-size-sm); }

  :host([size="md"]) button,
  :host([size="md"]) a { padding: var(--ctl-button-padding-md); font-size: var(--font-size-sm); }

  :host([size="lg"]) button,
  :host([size="lg"]) a { padding: var(--ctl-button-padding-lg); font-size: var(--font-size-md); }

  /* Variants - Use semantic color tokens */
  :host([variant="primary"]) button,
  :host([variant="primary"]) a {
    background: var(--ctl-color-brand);
    color: var(--ctl-color-on-brand);
  }

  :host([variant="secondary"]) button,
  :host([variant="secondary"]) a {
    background: var(--ctl-color-surface);
    color: var(--ctl-color-text);
    border-color: var(--ctl-color-border);
  }

  :host([variant="tertiary"]) button,
  :host([variant="tertiary"]) a {
    background: transparent;
    color: var(--ctl-color-text);
  }

  :host([variant="danger"]) button,
  :host([variant="danger"]) a {
    background: var(--ctl-color-danger);
    color: var(--ctl-color-on-danger);
  }

  :host([disabled]) button,
  :host([disabled]) a {
    opacity: var(--ctl-opacity-disabled);
    pointer-events: none;
    cursor: not-allowed;
  }

  :host([loading]) button,
  :host([loading]) a {
    cursor: progress;
  }

  .loader {
    width: 1em;
    height: 1em;
    border-radius: 50%;
    border: 2px solid color-mix(in srgb, currentColor 40%, transparent);
    border-top-color: currentColor;
    animation: spin 600ms linear infinite;
  }

  @keyframes spin { to { transform: rotate(360deg); } }

  ::slotted([slot="icon"]),
  ::slotted([slot="suffix"]) {
    display: inline-flex;
    align-items: center;
  }
</style>

<span id="root"></span>
`;

/**
 * Button Web Component
 * 
 * @class CtlButton
 * @extends HTMLElement
 * 
 * @attr {string} variant - Button style variant: "primary" | "secondary" | "tertiary" | "danger"
 * @attr {string} size - Button size: "sm" | "md" | "lg" (default: "md")
 * @attr {string} href - URL to navigate to (renders as <a> tag)
 * @attr {string} type - Button type: "button" | "submit" | "reset" (default: "button")
 * @attr {boolean} disabled - Disables the button
 * @attr {boolean} loading - Shows loading spinner and disables interaction
 * 
 * @slot - Default slot for button text
 * @slot icon - Leading icon slot
 * @slot suffix - Trailing icon or element slot
 * 
 * @fires ctl-click - Dispatched when button is clicked (unless disabled or loading). Event detail includes {href, originalEvent}.
 * 
 * @csspart root - The root button or anchor element
 * 
 * @cssprop --ctl-btn-radius - Button border radius (default: 8px)
 * @cssprop --ctl-btn-font-weight - Button font weight (default: 600)
 * @cssprop --ctl-btn-gap - Gap between icon and text (default: 8px)
 * @cssprop --ctl-color-primary - Primary color (default: #4f46e5)
 * @cssprop --ctl-color-danger - Danger color (default: #dc2626)
 */
export class CtlButton extends HTMLElement {
  static get observedAttributes() {
    return ["href", "disabled", "loading", "type"];
  }

  constructor() {
    super();
    this.attachShadow({ mode: "open" }).appendChild(template.content.cloneNode(true));
    this._root = this.shadowRoot.getElementById("root");
    this._onClick = this._onClick.bind(this);
    this._onDisabledClick = this._onDisabledClick.bind(this);
    this._buttonEl = null; // Cache button element for selective updates
  }

  connectedCallback() {
    this._render();
  }

  disconnectedCallback() {
    const el = this._root.querySelector("button, a");
    if (el) {
      el.removeEventListener("click", this._onClick);
      el.removeEventListener("click", this._onDisabledClick);
    }
  }

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;
    
    // For consistent behavior across all states (including loading true → false),
    // use a full re-render for relevant attribute changes.
    // Href change may require button ↔ anchor switch; other attributes
    // also re-use the same render path to avoid divergence.
    this._render();
  }

  _render() {
    // Full render (used for connectedCallback and attribute changes)
    const href = this.getAttribute("href");
    const disabled = this.hasAttribute("disabled");
    const loading = this.hasAttribute("loading");

    const tag = href ? "a" : "button";
    const el = document.createElement(tag);

    if (tag === "a") {
      if (!disabled) {
        el.href = href;
      }
      el.setAttribute("role", "button");
      if (disabled) {
        el.setAttribute("aria-disabled", "true");
        el.setAttribute("tabindex", "-1");
        el.addEventListener("click", this._onDisabledClick);
      }
    } else {
      el.type = this.getAttribute("type") || "button";
      if (disabled) el.disabled = true;
    }

    el.setAttribute("aria-busy", loading ? "true" : "false");
    if (loading) {
      const existingLabel = this.getAttribute("aria-label");
      const label = existingLabel || this.textContent?.trim() || "Button";
      el.setAttribute("aria-label", `${label} (loading)`);
    } else if (this.hasAttribute("aria-label")) {
      el.setAttribute("aria-label", this.getAttribute("aria-label"));
    }
    el.addEventListener("click", this._onClick);

    el.innerHTML = `
      ${loading ? `<span class="loader" aria-hidden="true"></span>` : `<slot name="icon"></slot>`}
      <slot></slot>
      <slot name="suffix"></slot>
    `;

    this._root.replaceChildren(el);
    this._buttonEl = el; // Cache the new element
  }

  _onDisabledClick(e) {
    e.preventDefault();
    e.stopPropagation();
    e.stopImmediatePropagation();
  }

  _onClick(e) {
    if (this.hasAttribute("disabled") || this.hasAttribute("loading")) {
      e.preventDefault();
      e.stopImmediatePropagation();
      return;
    }

    dispatchEvent(this, "ctl-click", {
      href: this.getAttribute("href") || null,
      originalEvent: e
    });
  }
}


if (!customElements.get("ctl-button")) {
  customElements.define("ctl-button", CtlButton);
}
